import { isEAN } from './eanChecker';

/**
 * Validación de cliente del alta manual de productos.
 *
 * Espeja las reglas del back (`src/helpers/productCodes.ts` y
 * `joiValidatorNewProduct`). El servidor sigue siendo la autoridad —de hecho el
 * formulario llama a `POST /products/validate` antes de crear—, pero comprobar
 * aquí lo que no necesita BD evita un viaje de red por cada errata.
 *
 * Lo que NO se puede validar en cliente y solo sabe el servidor: si la familia,
 * el SKU, el EAN o el UPC ya existen en el catálogo.
 *
 * Devuelve un objeto plano `{ [campo]: mensaje }` con las mismas rutas que usa
 * el back (`family`, `variants[0].sku`…), para poder fusionar ambas fuentes.
 */

export const FAMILY_PATTERN = /^\d{5}-\d{4}$/;
export const SKU_PATTERN = /^\d{5}-\d{4}-\d{3}$/;
export const SKU_SUFFIX_PATTERN = /^\d{3}$/;

export const FAMILY_FORMAT_HINT = 'AAAAA-BBBB (5 dígitos, guion, 4 dígitos)';

/** Prefijo que el sistema reserva para las familias renombradas por colisión de SHOPI. */
export const RESERVED_FAMILY_PREFIX = '5';

export const isValidFamily = (family) => FAMILY_PATTERN.test(family || '');
export const isReservedFamily = (family) => String(family || '').startsWith(RESERVED_FAMILY_PREFIX);

/** Compone el sku a partir de la familia y el sufijo de talla. */
export const composeSku = (family, suffix) => (family && suffix ? `${family}-${suffix}` : '');

/** Extrae el sufijo de talla de un sku bien formado: `00374-1200-036` → `036`. */
export const skuSuffix = (sku) => (SKU_PATTERN.test(sku || '') ? String(sku).slice(11) : '');

const isPositiveNumber = (value) => {
  if (value === '' || value === null || value === undefined) return false;
  const n = Number(value);
  return Number.isFinite(n) && n > 0;
};

/** Valores que aparecen más de una vez (ignorando vacíos). */
const duplicatesOf = (values) => {
  const seen = new Set();
  const duplicated = new Set();

  values.filter(Boolean).forEach((value) => {
    if (seen.has(value)) duplicated.add(value);
    else seen.add(value);
  });

  return duplicated;
};

/**
 * @param {object} form estado del formulario
 * @returns {{ [field: string]: string }} errores por campo. Vacío si todo va bien.
 */
export const validateProductForm = (form) => {
  const errors = {};
  const variants = form.variants || [];

  // ---------- Producto padre ----------
  if (!String(form.title || '').trim()) errors.title = 'El nombre del producto es obligatorio';

  const family = String(form.family || '').trim();
  if (!family) errors.family = 'La familia es obligatoria';
  else if (!isValidFamily(family)) errors.family = `La familia debe tener el formato ${FAMILY_FORMAT_HINT}`;
  else if (isReservedFamily(family))
    errors.family = `La familia no puede empezar por ${RESERVED_FAMILY_PREFIX}: ese prefijo lo reserva el sistema para las familias renombradas por colisión`;

  if (!String(form.gender || '').trim()) errors.gender = 'El género es obligatorio';
  if (!String(form.category || '').trim()) errors.category = 'La categoría es obligatoria';
  if (!String(form.season || '').trim()) errors.season = 'La temporada es obligatoria';
  if (!String(form.year || '').trim()) errors.year = 'El año es obligatorio';
  // subcategoría y fotos son opcionales a propósito

  // ---------- Variantes ----------
  if (variants.length === 0) {
    errors.variants = 'El producto debe tener al menos una variante';
    return errors;
  }

  const duplicatedSkus = duplicatesOf(variants.map((v) => v.sku));
  const duplicatedBarcodes = duplicatesOf(variants.map((v) => v.barcode));
  const duplicatedUpcs = duplicatesOf(variants.map((v) => v.upcEph));

  variants.forEach((variant, index) => {
    const at = (field) => `variants[${index}].${field}`;

    if (!String(variant.option || '').trim()) errors[at('option')] = 'La opción (talla) es obligatoria';

    const sku = String(variant.sku || '').trim();
    if (!sku) errors[at('sku')] = 'El SKU es obligatorio';
    else if (!SKU_PATTERN.test(sku)) errors[at('sku')] = 'El SKU debe tener el formato AAAAA-BBBB-CCC';
    else if (family && !sku.startsWith(`${family}-`))
      errors[at('sku')] = `El SKU debe empezar por la familia (${family}-)`;
    else if (duplicatedSkus.has(sku)) errors[at('sku')] = `El SKU ${sku} está repetido en este producto`;

    const barcode = String(variant.barcode || '').trim();
    if (!barcode) errors[at('barcode')] = 'El EAN es obligatorio';
    else if (!/^\d{13}$/.test(barcode)) errors[at('barcode')] = 'El EAN debe tener 13 dígitos';
    else if (!isEAN(barcode)) errors[at('barcode')] = 'El EAN no es válido: el dígito de control no cuadra';
    else if (duplicatedBarcodes.has(barcode)) errors[at('barcode')] = `El EAN ${barcode} está repetido en este producto`;

    // El UPC es opcional, pero si se rellena tampoco puede repetirse
    const upc = String(variant.upcEph || '').trim();
    if (upc && duplicatedUpcs.has(upc)) errors[at('upcEph')] = `El UPC ${upc} está repetido en este producto`;

    if (!isPositiveNumber(variant.pvp)) errors[at('pvp')] = 'El precio es obligatorio y debe ser mayor que cero';
    if (!isPositiveNumber(variant.cost)) errors[at('cost')] = 'El coste es obligatorio y debe ser mayor que cero';
  });

  return errors;
};

/**
 * Convierte la respuesta de `POST /products/validate`
 * (`[{ field, index?, message }]`) al mismo mapa plano que devuelve
 * `validateProductForm`, para poder pintarlos con el mismo código.
 */
export const serverErrorsToFieldMap = (errors = []) =>
  errors.reduce((acc, { field, message }) => {
    // Nos quedamos con el primer error de cada campo: es el más específico y
    // apilarlos en un solo input no ayuda a nadie.
    if (field && !acc[field]) acc[field] = message;
    return acc;
  }, {});
